import { useEffect, useMemo, useState } from "react";
import { Send, X, Loader2, Plus, Copy, Check, Gift } from "lucide-react";
import { api, formatApiError } from "../api";
import { useEnquiry } from "../context/EnquiryContext";
import Reveal from "./Reveal";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", institution: "", role: "", purpose: "", referral_code: "", wants_coa: null, declaration: false };
const MIN_VIALS = 10;

export default function EnquiryForm() {
    const [form, setForm] = useState(EMPTY);
    const [products, setProducts] = useState([]);
    const [pickerSlug, setPickerSlug] = useState("");
    const [customName, setCustomName] = useState("");
    const [sending, setSending] = useState(false);
    const [submittedCode, setSubmittedCode] = useState(null);
    const [copied, setCopied] = useState(false);
    const { selected, has, add, addCustom, remove, setStrength, setVials, clear } = useEnquiry();

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    const selectedItems = useMemo(
        () =>
            selected
                .map((x) => {
                    const p = products.find((pr) => pr.slug === x.slug);
                    if (p) return { ...p, chosenStrength: x.strength, vials: x.vials };
                    if (x.customName) {
                        return { slug: x.slug, name: x.customName, strengths: null, chosenStrength: null, vials: x.vials, custom: true };
                    }
                    return null;
                })
                .filter(Boolean),
        [selected, products]
    );

    const available = useMemo(() => products.filter((p) => !has(p.slug)), [products, has]);

    const totalVials = useMemo(
        () => selectedItems.reduce((sum, p) => sum + (Number(p.vials) || 0), 0),
        [selectedItems]
    );

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (selected.length === 0) {
            toast.error("Add at least one compound to your enquiry");
            return;
        }
        const underMin = selectedItems.filter((p) => !p.vials || Number(p.vials) < MIN_VIALS);
        if (underMin.length > 0) {
            toast.error(`Minimum order is ${MIN_VIALS} vials per compound — check: ${underMin.map((p) => p.name).join(", ")}`);
            return;
        }
        if (form.wants_coa === null) {
            toast.error("Please choose yes or no for Certificates of Analysis (COAs)");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post("/enquiries", {
                name: form.name,
                email: form.email,
                institution: form.institution,
                role: form.role,
                compounds: selectedItems.map((p) => {
                    const dose = p.chosenStrength ? `${p.name} — ${p.chosenStrength}` : p.name;
                    return `${dose} × ${p.vials} vials`;
                }),
                quantity: `${totalVials} vials total`,
                purpose: form.purpose,
                referral_code: form.referral_code.trim() || null,
                wants_coa: form.wants_coa,
                declaration: form.declaration,
            });
            setSubmittedCode(data.referral_code);
            setForm(EMPTY);
            clear();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(submittedCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Could not copy — please copy it manually");
        }
    };

    const inputCls =
        "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100 transition-all duration-300";

    return (
        <section id="enquiry" data-testid="enquiry-section" className="py-16 md:py-24 bg-white border-t border-slate-200">
            <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
                <Reveal>
                    <div className="text-center mb-12">
                        <div className="font-jbm text-xs uppercase tracking-[0.25em] text-cyan-700 mb-3">05 — B2B enquiry</div>
                        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
                            Request supply
                        </h2>
                        <p className="mt-4 text-base text-slate-500 max-w-xl mx-auto leading-relaxed">
                            Select your compounds, doses and vial counts, then submit the request — we reply to your
                            email with prices, payment methods and batch documentation within 48 hours.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    {submittedCode ? (
                        <div className="bg-[#F8FAFC] border border-slate-200 rounded-3xl p-8 sm:p-12 text-center" data-testid="enquiry-success-panel">
                            <div className="w-14 h-14 rounded-full bg-cyan-50 border border-cyan-200 flex items-center justify-center mx-auto mb-5">
                                <Check className="w-6 h-6 text-cyan-700" />
                            </div>
                            <h3 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Enquiry received</h3>
                            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
                                We've emailed you a confirmation. We reply within 48 hours with the full price for
                                everything you requested, plus payment methods.
                            </p>
                            <div className="mt-8 bg-slate-900 rounded-2xl p-6 max-w-sm mx-auto">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Gift className="w-4 h-4 text-cyan-400" />
                                    <span className="font-jbm text-[10px] uppercase tracking-widest text-slate-400">Your referral code</span>
                                </div>
                                <div className="font-jbm text-2xl font-bold tracking-[0.2em] text-white" data-testid="referral-code-display">
                                    {submittedCode}
                                </div>
                                <button
                                    data-testid="copy-referral-code-button"
                                    onClick={copyCode}
                                    className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-slate-300 border border-white/20 rounded-full px-4 py-2 hover:bg-white/10 transition-colors duration-300"
                                >
                                    {copied ? <Check className="w-3.5 h-3.5 text-cyan-400" /> : <Copy className="w-3.5 h-3.5" />}
                                    {copied ? "Copied" : "Copy code"}
                                </button>
                                <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                                    Share it with other researchers — when their first order is confirmed, you receive
                                    credit towards your next order.
                                </p>
                            </div>
                            <button
                                data-testid="submit-another-enquiry-button"
                                onClick={() => setSubmittedCode(null)}
                                className="mt-8 text-sm font-medium text-slate-600 hover:text-cyan-800 transition-colors duration-300 underline underline-offset-4"
                            >
                                Submit another enquiry
                            </button>
                        </div>
                    ) : (
                    <form onSubmit={submit} className="bg-[#F8FAFC] border border-slate-200 rounded-3xl p-6 sm:p-10 space-y-5" data-testid="enquiry-form">
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Researcher name *</label>
                                <input data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Dr Jane Citizen" />
                            </div>
                            <div>
                                <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Email *</label>
                                <input data-testid="enquiry-email-input" required type="text" value={form.email} onChange={set("email")} className={inputCls} placeholder="Your email — we reply here with prices" />
                            </div>
                            <div>
                                <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Institution / company *</label>
                                <input data-testid="enquiry-institution-input" required value={form.institution} onChange={set("institution")} className={inputCls} placeholder="University of Melbourne" />
                            </div>
                            <div>
                                <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Role / department *</label>
                                <input data-testid="enquiry-role-input" required value={form.role} onChange={set("role")} className={inputCls} placeholder="Principal Investigator, R&D" />
                            </div>
                        </div>

                        <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3" data-testid="enquiry-min-order-disclaimer">
                            <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                            <p className="text-sm font-semibold tracking-wide text-amber-800">
                                Minimum order: you can only request a minimum of 10 vials of each peptide you select.
                            </p>
                        </div>

                        <div>
                            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                                <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500">Selected compounds, doses & vials *</label>
                                <span className="hidden sm:inline font-jbm text-[10px] uppercase tracking-widest text-cyan-700">Minimum {MIN_VIALS} vials per compound</span>
                            </div>
                            <div className="bg-white border border-slate-200 rounded-xl p-3 min-h-[64px] space-y-2" data-testid="enquiry-compounds-chips">
                                {selectedItems.length === 0 && (
                                    <span className="text-sm text-slate-400 px-2 block py-1.5">No compounds selected — browse the catalog or add below.</span>
                                )}
                                {selectedItems.map((p) => (
                                    <div key={p.slug} className="flex items-center gap-2 flex-wrap bg-slate-900 text-white rounded-xl pl-4 pr-2 py-2" data-testid={`selected-compound-${p.slug}`}>
                                        <span className="text-sm font-medium shrink-0">{p.name}</span>
                                        <div className="ml-auto flex items-center gap-2">
                                            {p.custom ? (
                                                <span className="font-jbm text-[10px] uppercase tracking-widest text-cyan-300">Custom request</span>
                                            ) : p.strengths ? (
                                                <select
                                                    data-testid={`dose-select-${p.slug}`}
                                                    value={p.chosenStrength || p.strengths[0]}
                                                    onChange={(e) => setStrength(p.slug, e.target.value)}
                                                    aria-label={`Dose for ${p.name}`}
                                                    className="bg-white/10 border border-white/20 text-white text-xs font-jbm rounded-lg px-2.5 py-1.5 outline-none focus:border-cyan-400 cursor-pointer [&>option]:text-slate-900"
                                                >
                                                    {p.strengths.map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="font-jbm text-[10px] uppercase tracking-widest text-white/60">Size on enquiry</span>
                                            )}
                                            <span className="flex items-center bg-white/10 border border-white/20 rounded-lg overflow-hidden">
                                                <input
                                                    data-testid={`vials-input-${p.slug}`}
                                                    type="number"
                                                    min={MIN_VIALS}
                                                    step={1}
                                                    value={p.vials}
                                                    onChange={(e) => setVials(p.slug, e.target.value)}
                                                    aria-label={`Vials of ${p.name} (minimum ${MIN_VIALS})`}
                                                    className="w-16 bg-transparent text-white text-xs font-jbm px-2.5 py-1.5 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                />
                                                <span className="font-jbm text-[10px] uppercase tracking-wider text-white/60 pr-2.5">vials</span>
                                            </span>
                                            <button
                                                type="button"
                                                data-testid={`remove-compound-${p.slug}`}
                                                onClick={() => remove(p.slug)}
                                                aria-label={`Remove ${p.name}`}
                                                className="w-7 h-7 shrink-0 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                                <select
                                    data-testid="enquiry-compound-select"
                                    value={pickerSlug}
                                    onChange={(e) => setPickerSlug(e.target.value)}
                                    className={`${inputCls} flex-1`}
                                >
                                    <option value="">Add a compound…</option>
                                    {available.map((p) => (
                                        <option key={p.slug} value={p.slug}>{p.name} — {p.category}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    data-testid="enquiry-add-compound-button"
                                    onClick={() => {
                                        if (pickerSlug) {
                                            const p = products.find((pr) => pr.slug === pickerSlug);
                                            add(pickerSlug, p?.strengths?.[0] || null);
                                            setPickerSlug("");
                                        }
                                    }}
                                    className="shrink-0 flex items-center justify-center gap-2 border border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors duration-300"
                                >
                                    <Plus className="w-4 h-4" /> Add
                                </button>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 mt-2">
                                <input
                                    data-testid="enquiry-custom-compound-input"
                                    value={customName}
                                    onChange={(e) => setCustomName(e.target.value)}
                                    className={`${inputCls} flex-1`}
                                    placeholder="Not in the catalog? Type any compound name…"
                                />
                                <button
                                    type="button"
                                    data-testid="enquiry-add-custom-button"
                                    onClick={() => {
                                        if (customName.trim()) {
                                            addCustom(customName.trim());
                                            setCustomName("");
                                        }
                                    }}
                                    className="shrink-0 flex items-center justify-center gap-2 border border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-cyan-800 hover:text-white hover:border-cyan-800 transition-colors duration-300"
                                >
                                    <Plus className="w-4 h-4" /> Request
                                </button>
                            </div>
                            {selectedItems.length > 0 && (
                                <p className="font-jbm text-xs text-slate-500 mt-2" data-testid="enquiry-total-vials">
                                    Total: {totalVials} vials across {selectedItems.length} compound{selectedItems.length !== 1 ? "s" : ""}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Referral code (optional)</label>
                            <input
                                data-testid="enquiry-referral-input"
                                value={form.referral_code}
                                onChange={set("referral_code")}
                                className={`${inputCls} font-jbm uppercase tracking-widest`}
                                placeholder="AUS-XXXXXX"
                            />
                        </div>

                        <div>
                            <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">
                                Do you want COAs for the compounds you chose? *
                            </label>
                            <div className="grid grid-cols-2 gap-3" data-testid="enquiry-coa-toggle">
                                <button
                                    type="button"
                                    data-testid="enquiry-coa-yes"
                                    onClick={() => setForm((f) => ({ ...f, wants_coa: true }))}
                                    className={`py-3.5 rounded-xl border text-sm font-medium transition-colors duration-300 ${
                                        form.wants_coa === true
                                            ? "bg-cyan-700 border-cyan-700 text-white"
                                            : "bg-white border-slate-200 text-slate-600 hover:border-cyan-700 hover:text-cyan-800"
                                    }`}
                                >
                                    Yes — send Certificates of Analysis
                                </button>
                                <button
                                    type="button"
                                    data-testid="enquiry-coa-no"
                                    onClick={() => setForm((f) => ({ ...f, wants_coa: false }))}
                                    className={`py-3.5 rounded-xl border text-sm font-medium transition-colors duration-300 ${
                                        form.wants_coa === false
                                            ? "bg-slate-900 border-slate-900 text-white"
                                            : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                                    }`}
                                >
                                    No, not needed
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="font-jbm text-[10px] uppercase tracking-widest text-slate-500 block mb-2">Research purpose declaration *</label>
                            <textarea
                                data-testid="enquiry-purpose-input"
                                required
                                rows={4}
                                value={form.purpose}
                                onChange={set("purpose")}
                                className={`${inputCls} resize-none`}
                                placeholder="Briefly describe the in-vitro research application, ethics approval reference (if applicable) and intended analytical use…"
                            />
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="enquiry-declaration-label">
                            <input
                                data-testid="enquiry-declaration-checkbox"
                                type="checkbox"
                                checked={form.declaration}
                                onChange={set("declaration")}
                                className="mt-1 w-4 h-4 accent-cyan-700"
                                required
                            />
                            <span className="text-sm leading-relaxed text-slate-600">
                                I declare these materials are requested for legitimate in-vitro laboratory research only,
                                and will not be used in humans or animals outside approved ethics protocols.
                            </span>
                        </label>

                        <button
                            data-testid="enquiry-submit-button"
                            type="submit"
                            disabled={sending}
                            className="w-full flex items-center justify-center gap-2.5 bg-slate-900 text-white font-medium py-4 rounded-full hover:bg-cyan-800 disabled:opacity-60 transition-colors duration-300"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {sending ? "Submitting…" : "Submit research enquiry"}
                        </button>
                    </form>
                    )}
                </Reveal>
            </div>
        </section>
    );
}
